import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import SurveyContext from '@totem/components/surveyV2/instanceDetail/SurveyInstanceContext';
import {
  QuestionValue,
  RequestSurveyInstanceQuestionAnswer,
  SurveyError,
  SurveyQueryResult,
} from '@totem/components/surveyV2/types';
import { isNotNull } from '@totem/utilities/common';
import { UNAUTHENTICATED_SURVEY_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  token: string;
  children?: ReactNode;
};

const NaSurveyContainer = ({ token, children }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [data, setData] = useState<SurveyQueryResult>(null);
  const [errorAlertVisible, setErrorAlertVisible] = useState<boolean>(false);
  const [errors, setErrors] = useState<SurveyError[]>([]);
  const [filterAnswered, setFilterAnswered] = useState<boolean>(false);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);

      setIsLoading(true);

      fetch(`${UNAUTHENTICATED_SURVEY_ENDPOINT}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
      })
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [token, refresh]);

  const sendQuestionAnswer = useCallback(
    async (
      surveyId: string,
      categoryId: string,
      questionId: string,
      newQuestionValue: QuestionValue,
      newAdditionalContext: string,
    ) => {
      const request: RequestSurveyInstanceQuestionAnswer = {
        surveyInstanceId: surveyId,
        categoryId,
        questionId,
        answer: newQuestionValue,
        additionalContext: newAdditionalContext,
      };

      fetch(`${UNAUTHENTICATED_SURVEY_ENDPOINT}/question`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(request),
      })
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  const sendNewCategoryInstanceRequest = useCallback(
    async (surveyId: string, categoryId: string, displayName: string) => {
      const payload = { displayName };

      fetch(
        `${UNAUTHENTICATED_SURVEY_ENDPOINT}/category/${categoryId}/instance`,
        {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(payload),
        },
      )
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  const sendDeleteCategoryInstanceRequest = useCallback(
    async (surveyId: string, categoryId: string, instanceNumber: number) => {
      fetch(
        `${UNAUTHENTICATED_SURVEY_ENDPOINT}/category/${categoryId}/instance/${instanceNumber}`,
        {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${token}`,
          }),
        },
      )
        .then((res) => res.json())
        .then((result: SurveyQueryResult) => {
          setData(result);
        })
        .then(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  const sendSubmitReopen = useCallback(async (action: string) => {
    const payload = {};
    fetch(`${UNAUTHENTICATED_SURVEY_ENDPOINT}/${action}`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result: SurveyQueryResult) => {
        setData(result);
      });
  }, []);

  const submit = (surveyId: string, action: string) => {
    sendSubmitReopen(action);
  };

  const handleOnAction = (newAction: string) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);
        break;
      default:
        break;
    }
  };

  const createCategoryInstance = (
    surveyId: string,
    categoryId: string,
    displayName: string,
  ) => {
    sendNewCategoryInstanceRequest(surveyId, categoryId, displayName);
  };

  const deleteCategoryInstance = (
    surveyId: string,
    categoryId: string,
    instanceNumber: number,
  ) => {
    sendDeleteCategoryInstanceRequest(surveyId, categoryId, instanceNumber);
  };

  const updateQuestionAnswer = (
    categoryId: string,
    questionId: string,
    newQuestionValue: QuestionValue,
    newAdditionalContext: string,
  ) => {
    sendQuestionAnswer(
      data.survey.id,
      categoryId,
      questionId,
      newQuestionValue,
      newAdditionalContext,
    );
  };

  return (
    <SurveyContext.Provider
      value={{
        surveyInstanceId:
          isNotNull(data) && isNotNull(data.survey) ? data.survey.id : '',
        loading: isLoading,
        setIsLoading,
        data,
        setData,
        updateQuestionAnswer,
        createCategoryInstance,
        deleteCategoryInstance,
        submit,
        errorAlertVisible,
        setErrorAlertVisible,
        errors,
        setErrors,
        filterAnswered,
        setFilterAnswered,
        onAction: handleOnAction,
        isAuthenticated: false,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};

export default NaSurveyContainer;
